import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { EnumDropdownItemVariant } from 'shared/ui'
import { ComplianceApi, ICountriesEnum } from 'entities/Compliance'
import type { InternationalStepsStore } from 'pages/settings/pages/compliance/pages/international/store/internationalStepsStore'
import {
  RegistrationAuthoritySelectStore,
  type RegistrationAuthorityConfig,
} from 'pages/settings/pages/compliance/ui/RegistrationAuthoritySelect'

const internationalRegistrationAuthorityConfig: RegistrationAuthorityConfig = {
  countries: [
    {
      label: 'UK: Company Registration Number (CRN)',
      id: ICountriesEnum.GB,
      iconL: 'flagUK',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'US: Employer Identification Number (EIN)',
      id: ICountriesEnum.US,
      iconL: 'flagUSA',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'CA: Canadian Business Number (CBN)',
      id: ICountriesEnum.CA,
      iconL: 'flagCanada',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'AU: Australian Company Number (ACN)',
      id: ICountriesEnum.AU,
      iconL: 'flagAU',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
    {
      label: 'Other',
      id: ICountriesEnum.OTHER,
      iconL: 'globe',
      variant: EnumDropdownItemVariant.CheckedRight,
    },
  ],
}

export class BusinessInfoStore {
  private readonly _registrationAuthorityStore: RegistrationAuthoritySelectStore

  constructor(private _internationalStepsStore: InternationalStepsStore) {
    this._registrationAuthorityStore = new RegistrationAuthoritySelectStore(
      internationalRegistrationAuthorityConfig
    )

    makeAutoObservable(this)
  }

  private _businessName: string | null = null
  private _einSearch = ''
  private _websiteUrl: string | null = null
  private _socialMediaUrl: string | null = null
  private _isCorrectUrl = false
  private _noWebsiteUrl = false
  private _websiteUrlLocalError = ''
  private _socialMediaUrlError = ''
  private _needCheckUrlAfterChange = false
  private _einErrorMessage = ''

  get einItem() {
    return this._internationalStepsStore.einItem
  }

  get businessName() {
    return this._businessName
  }

  get registrationAuthorityStore() {
    return this._registrationAuthorityStore
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get isCorrectUrl() {
    return this._isCorrectUrl
  }

  get hasWebsiteUrl() {
    return !!(this.websiteUrl?.length && this.isCorrectUrl)
  }

  get socialMediaUrl() {
    return this._socialMediaUrl
  }

  get noWebsiteUrl() {
    return this._noWebsiteUrl
  }

  get websiteUrlLocalError() {
    return this._websiteUrlLocalError
  }

  get socialMediaUrlError() {
    return this._socialMediaUrlError
  }

  get einFormatted() {
    const ein = this._einSearch.replace(/[^0-9]/g, '')

    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.US:
        return ein.length <= 2 ? this._einSearch : `${ein.slice(0, 2)}-${ein.slice(2)}`
      case ICountriesEnum.AU:
        return ein.match(/.{1,3}/g)?.join(' ')
      default:
        return ein
    }
  }

  get einOrigin() {
    return this._einSearch
  }

  get einPlaceholder() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.US:
        return 'e.g. 12-3456789'
      case ICountriesEnum.CA:
        return 'e.g. 123456789'
      case ICountriesEnum.AU:
        return 'e.g. 123 456 789'
      default:
        return 'e.g. 12345678'
    }
  }

  get einLength() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.GB:
        return 8
      case ICountriesEnum.US:
        return 9
      case ICountriesEnum.CA:
        return 9
      case ICountriesEnum.AU:
        return 9
      default:
        return null
    }
  }

  get selectedCountryKey() {
    switch (this.registrationAuthorityStore.country) {
      case ICountriesEnum.GB:
        return 'UK:CRN'
      case ICountriesEnum.US:
        return 'US:EIN'
      case ICountriesEnum.CA:
        return 'CA:CBN'
      case ICountriesEnum.AU:
        return 'AU:ACN'
      case ICountriesEnum.OTHER:
        return 'OTHER'
      default:
        return null
    }
  }

  get isNextDisabled() {
    if (!this.registrationAuthorityStore.country) return true
    if (!this._businessName) return true
    if (!this.einOrigin) return true

    const hasValidUrl = this._websiteUrl || (this._noWebsiteUrl && this._socialMediaUrl)
    if (!hasValidUrl) return true

    return this.einLength && this.einOrigin.length !== this.einLength
  }

  get einErrorMessage() {
    return this._einErrorMessage
  }

  get einErrorMessageText() {
    return `Must be a ${this.einLength}-digit number ${this.einPlaceholder}`
  }

  init = () => {
    if (this.einItem) {
      this._businessName = this.einItem.business_name ?? null
      this._einSearch = this.einItem.ein?.replace(/[^0-9]/g, '') ?? ''
      this._websiteUrl = this.einItem?.website_url ?? null
      this._socialMediaUrl = this.einItem?.social_media_url ?? null

      if (this.einItem.country && !this._registrationAuthorityStore.country) {
        this._registrationAuthorityStore.setCountry(this.einItem.country)
      }
    }
  }

  setEinSearch = (search: string) => {
    const stringOrigin = search.replace(/[^0-9]/g, '')

    this.setEinErrorMessage('')

    if (this.einLength && stringOrigin.length > this.einLength) {
      this.setEinErrorMessage(this.einErrorMessageText)
    }

    this._einSearch = stringOrigin
  }

  checkEin = () => {
    if (this._einSearch.length && this.einLength && this._einSearch.length < this.einLength) {
      this.setEinErrorMessage(this.einErrorMessageText)
    }
  }

  setEinErrorMessage = (message: string) => {
    this._einErrorMessage = message
  }

  setIsCorrectUrl = (value: boolean) => {
    this._isCorrectUrl = value
  }

  setWebsiteUrl = (url: string | null, fromRequest?: boolean) => {
    this._needCheckUrlAfterChange = !fromRequest
    this.setIsCorrectUrl(false)

    if (this._websiteUrlLocalError) {
      this.setWebsiteUrlLocalError('')
    }
    if (fromRequest && url?.length) {
      this.setIsCorrectUrl(true)
    }
    const einItem = { ...this.einItem }

    einItem.website_url = url

    this._websiteUrl = url
    this._internationalStepsStore.updateEinItem(einItem)
  }

  setWebsiteUrlLocalError = (error: string) => {
    this._websiteUrlLocalError = error
  }

  setSocialMediaUrlError = (error: string) => {
    this._socialMediaUrlError = error
  }

  setSocialMediaUrl = (url: string | null) => {
    if (this._socialMediaUrlError) {
      this.setSocialMediaUrlError('')
    }

    const einItem = { ...this.einItem }

    einItem.social_media_url = url

    this._socialMediaUrl = url
    this._internationalStepsStore.updateEinItem(einItem)
  }

  checkUrl = async (url: string, type: 'websiteUrl' | 'socialMediaUrl') => {
    const errorText = 'Please enter a valid url (e.g. https://www.example.com/)'

    const setErrorAction =
      type === 'websiteUrl' ? this.setWebsiteUrlLocalError : this.setSocialMediaUrlError

    try {
      const { data } = await ComplianceApi.onlineFormUrlCheck({ onlineFormUrl: url })

      if (!data.reachable && type === 'websiteUrl' && this._noWebsiteUrl) {
        // click to I don't have a website before response from backend
        return
      }

      if (!data.reachable) {
        setErrorAction(errorText)
      }

      this._isCorrectUrl = data.reachable
      this._needCheckUrlAfterChange = !data.reachable
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = error.errors.getError('message')

        if (message && type === 'websiteUrl' && this._noWebsiteUrl) {
          // click to I don't have a website before response from backend
          return
        }

        if (message) {
          setErrorAction(message)
        }
      }
      logger.error(error)
    }
  }

  setNoWebsiteUrl = (value: boolean) => {
    this._noWebsiteUrl = value
  }

  handleSetNoWebsiteUrl = () => {
    this.setWebsiteUrlLocalError('')
    this.setSocialMediaUrlError('')

    this.setWebsiteUrl(null)
    this.setSocialMediaUrl(null)
    this.setNoWebsiteUrl(!this._noWebsiteUrl)
  }

  addBusinessName = (business_name: string) => {
    const einItem = { ...this.einItem }

    einItem.business_name = business_name
    this._businessName = business_name

    this._internationalStepsStore.updateEinItem(einItem)
  }

  resetEinSearch = () => {
    this._einSearch = ''
    this.setEinErrorMessage('')
  }
}
